import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigContext } from '../context/GlobalContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BusinessDropdown from './child/BusinessDropdown';
import { useNavigate } from 'react-router-dom';
import * as actionType from "../context/actions";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];


const TAX_SLABS = [5, 12, 18, 28];

/* ── Reusable custom dropdown (Standardized style) ── */
const CustomDropdown = ({ label, value, options, onSelect, searchable = false, placeholder = "Select" }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        return options.filter(o => o.label?.toString().toLowerCase().includes(search.toLowerCase()));
    }, [options, search]);

    const selected = options.find(o => o.value === value);

    return (
        <div ref={ref} className="w-100">
            {label && <label className="form-label text-sm fw-semibold text-secondary-light mb-1">{label}</label>}
            <div className="dropdown">
                <button
                    className="btn btn-primary-600 bg-primary-50 border-primary-50 text-primary-600 dropdown-toggle w-100 d-flex justify-content-between align-items-center px-16 py-10 rounded"
                    type="button"
                    onClick={() => setOpen(prev => !prev)}
                >
                    <span style={{ fontSize: 14 }} className="text-truncate me-2">{selected?.label || placeholder}</span>
                </button>
                {open && (
                    <ul className="dropdown-menu w-100 show mt-1 shadow-sm border-0 rounded-3 text-center hide-scrollbar" style={{ padding: 0, minWidth: "100%", zIndex: 1050 }}>
                        {searchable && (
                            <li className="py-2 sticky-top z-2 bg-white">
                                <input
                                    type="text"
                                    className="form-control countrybackground mx-auto"
                                    style={{ width: "90%" }}
                                    placeholder={`Search...`}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    autoFocus
                                />
                            </li>
                        )}
                        <li style={{ maxHeight: "250px", overflowY: "auto" }} className="hide-scrollbar">
                            <ul className="list-unstyled mb-0">
                                {filtered.length > 0 ? (
                                    filtered.map(o => (
                                        <li key={o.value}>
                                            <a
                                                href="#"
                                                className={`dropdown-item d-flex align-items-center gap-2 px-16 py-10 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${value === o.value ? "bg-primary-50 fw-semibold" : ""}`}
                                                onClick={e => { e.preventDefault(); onSelect(o.value); setSearch(""); setOpen(false); }}
                                            >
                                                <span className="text-truncate">{o.label}</span>
                                                {value === o.value && <i className="bi bi-check2 text-primary-600 ms-auto"></i>}
                                            </a>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-center text-primary-600 small py-2">No results</li>
                                )}
                            </ul>
                        </li>
                    </ul>
                )}
            </div>
        </div>
    );
};

const Gstr3BLayer = () => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedCompanyId, setSelectedCompanyId] = useState(
        state?.BUSINESSES?.[0]?.companyId || ""
    );

    const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);
    const selectedBusiness = state?.BUSINESSES?.find(b => b.companyId === selectedCompanyId);

    const monthOptions = MONTH_NAMES.map((m, i) => ({ value: i, label: m }));
    const yearOptions = years.map(y => ({ value: y, label: y.toString() }));

    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    // ===========================
    // PLAN CONFIG
    // ===========================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";
    const isAgencyPlan = userPlanName === agencyPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;

    let allowedBusinessCount = 1; // Free & Starter
    if (isProfessionalPlan) allowedBusinessCount = 3;
    if (isAgencyPlan) allowedBusinessCount = sortedBusinesses.length;

    const handleBusinessChange = (companyId) => {
        setSelectedCompanyId(companyId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: companyId });
    };

    const filteredInvoices = useMemo(() => {
        if (!state?.INVOICES) return [];
        return state.INVOICES.filter(inv => {
            if (inv.companyId !== selectedCompanyId) return false;

            // Only display invoices where currency is INR (or empty, which defaults to INR)
            const currency = inv.currency ? inv.currency.toString().toUpperCase() : "INR";
            if (currency !== "INR") return false;

            const ts = parseInt(inv.date?.length > 10 ? inv.date : inv.date * 1000);
            const d = new Date(ts);
            return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
        });
    }, [state?.INVOICES, selectedCompanyId, selectedMonth, selectedYear]);

    const slabData = useMemo(() => {
        const slabMap = {};
        TAX_SLABS.forEach(slab => {
            slabMap[slab] = { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0, count: 0 };
        });
        slabMap["other"] = { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0, count: 0 };

        filteredInvoices.forEach(inv => {
            const taxableValue = parseFloat(inv.subtotal || 0);
            const gstPct = parseFloat(inv.gstPercetage || 0);
            const totalTax = parseFloat(inv.gstAmount || ((taxableValue * gstPct) / 100));
            const isIgst = inv.isigst === "true" || inv.isigst === true;
            const cgst = isIgst ? 0 : totalTax / 2;
            const sgst = isIgst ? 0 : totalTax / 2;
            const igst = isIgst ? totalTax : 0;
            const key = TAX_SLABS.includes(gstPct) ? gstPct : "other";
            slabMap[key].taxableValue += taxableValue;
            slabMap[key].cgst += cgst;
            slabMap[key].sgst += sgst;
            slabMap[key].igst += igst;
            slabMap[key].totalTax += totalTax;
            slabMap[key].count += 1;
        });
        return slabMap;
    }, [filteredInvoices]);

    const totals = useMemo(() => {
        const all = [...TAX_SLABS, "other"];
        return all.reduce(
            (acc, key) => ({
                taxableValue: acc.taxableValue + slabData[key].taxableValue,
                cgst: acc.cgst + slabData[key].cgst,
                sgst: acc.sgst + slabData[key].sgst,
                igst: acc.igst + slabData[key].igst,
                totalTax: acc.totalTax + slabData[key].totalTax,
            }),
            { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0 }
        );
    }, [slabData]);

    const sym = state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL}` : state?.SYMBOL;
    const fmt = (n) => n.toFixed(2);

    const handleExportPdf = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const businessName = selectedBusiness?.companyName || "All Businesses";
        const period = `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;

        // Header
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text("GSTR-3B Summary Report", pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Business: ${businessName}`, pageWidth / 2, 22, { align: 'center' });
        doc.text(`GSTIN: ${selectedBusiness?.gstNo || "N/A"}`, pageWidth / 2, 28, { align: 'center' });
        doc.text(`Period: ${period}`, pageWidth / 2, 34, { align: 'center' });

        // Section 3.1 Table
        doc.setFontSize(12);
        doc.setTextColor(40);
        doc.text("Section 3.1: Outward Supplies", 14, 45);

        const tableBody = TAX_SLABS.map(slab => [
            `Outward Supplies @ ${slab}%`,
            slabData[slab].count,
            fmt(slabData[slab].taxableValue),
            fmt(slabData[slab].cgst),
            fmt(slabData[slab].sgst),
            fmt(slabData[slab].igst),
            fmt(slabData[slab].totalTax)
        ]);

        if (slabData["other"].count > 0) {
            tableBody.push([
                "Other / Custom Rate",
                slabData["other"].count,
                fmt(slabData["other"].taxableValue),
                fmt(slabData["other"].cgst),
                fmt(slabData["other"].sgst),
                fmt(slabData["other"].igst),
                fmt(slabData["other"].totalTax)
            ]);
        }

        autoTable(doc, {
            startY: 50,
            head: [['Nature of Supply', 'Inv', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total Tax']],
            body: tableBody,
            foot: [['Total', filteredInvoices.length, fmt(totals.taxableValue), fmt(totals.cgst), fmt(totals.sgst), fmt(totals.igst), fmt(totals.totalTax)]],
            theme: 'grid',
            headStyles: { fillColor: [72, 127, 255], textColor: [255, 255, 255] },
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 8 },
        });

        // Section 6 Summary
        const finalY = doc.lastAutoTable.finalY + 15;
        doc.setFontSize(12);
        doc.text("Section 6: Tax Liability Summary", 14, finalY);

        const liabilityData = [
            ["Integrated Tax (IGST)", fmt(totals.igst)],
            ["Central Tax (CGST)", fmt(totals.cgst)],
            ["State / UT Tax (SGST)", fmt(totals.sgst)],
            ["Net Tax Payable", fmt(totals.totalTax)]
        ];

        autoTable(doc, {
            startY: finalY + 5,
            body: liabilityData,
            theme: 'plain',
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 100 }, 1: { halign: 'right' } }
        });

        doc.save(`GSTR-3B_${businessName.replace(/\s+/g, '_')}_${period.replace(/\s+/g, '_')}.pdf`);
    };

    const handleExportExcel = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        const headers = ['Nature of Supply', 'Invoices', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total Tax'];
        const csvRows = [
            [`GSTR-3B Report — ${MONTH_NAMES[selectedMonth]} ${selectedYear}`],
            [`Business: ${selectedBusiness?.companyName || "All"}`],
            [`GSTIN: ${selectedBusiness?.gstNo || "N/A"}`],
            [],
            headers,
            ...TAX_SLABS.map(slab => [
                `Outward Supplies @ ${slab}% GST`,
                slabData[slab].count,
                fmt(slabData[slab].taxableValue),
                fmt(slabData[slab].cgst),
                fmt(slabData[slab].sgst),
                fmt(slabData[slab].igst),
                fmt(slabData[slab].totalTax),
            ]),
            ...(slabData["other"].count > 0 ? [[
                'Other / Custom Rate',
                slabData["other"].count,
                fmt(slabData["other"].taxableValue),
                fmt(slabData["other"].cgst),
                fmt(slabData["other"].sgst),
                fmt(slabData["other"].igst),
                fmt(slabData["other"].totalTax),
            ]] : []),
            [],
            ['Total', filteredInvoices.length, fmt(totals.taxableValue), fmt(totals.cgst), fmt(totals.sgst), fmt(totals.igst), fmt(totals.totalTax)],
            [],
            ['--- Section 6: Tax Liability ---'],
            ['IGST', fmt(totals.igst)],
            ['CGST', fmt(totals.cgst)],
            ['SGST', fmt(totals.sgst)],
            ['Net Tax Payable', fmt(totals.totalTax)],
        ];
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GSTR-3B_${MONTH_NAMES[selectedMonth]}_${selectedYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleExportGstr3BCASummary = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }

        // Official Section 3.1 Headers
        const headers = [
            'Nature of Supply',
            'Total Taxable Value',
            'Integrated Tax',
            'Central Tax',
            'State/UT Tax',
            'Cess'
        ];

        const summaryRows = [
            [
                '(a) Outward taxable supplies (other than zero rated nil rated and exempted)',
                fmt(totals.taxableValue),
                fmt(totals.igst),
                fmt(totals.cgst),
                fmt(totals.sgst),
                '0.00'
            ],
            ['(b) Outward taxable supplies (zero rated)', '0.00', '0.00', '0.00', '0.00', '0.00'],
            ['(c) Other outward supplies (Nil rated exempted)', '0.00', '0.00', '0.00', '0.00', '0.00'],
            ['(d) Inward supplies (liable to reverse charge)', '0.00', '0.00', '0.00', '0.00', '0.00'],
            ['(e) Non-GST outward supplies', '0.00', '0.00', '0.00', '0.00', '0.00']
        ];

        const csvContent = [headers, ...summaryRows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const businessName = selectedBusiness?.companyName || "Business";
        a.download = `GSTR3B_Summary_${businessName.replace(/\s+/g, '_')}_${MONTH_NAMES[selectedMonth]}_${selectedYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const overviewCards = [
        { label: "Total Outward Supplies", value: fmt(totals.taxableValue), icon: "mdi:arrow-up-circle-outline", color: "#487fff", desc: "Taxable Value" },
        { label: "Total CGST", value: fmt(totals.cgst), icon: "mdi:percent", color: "#3dc4c4", desc: "Central Tax" },
        { label: "Total SGST", value: fmt(totals.sgst), icon: "mdi:percent", color: "#45c15d", desc: "State Tax" },
        { label: "Total IGST", value: fmt(totals.igst), icon: "mdi:percent", color: "#f4a60a", desc: "Integrated Tax" },
        { label: "Net Tax Payable", value: fmt(totals.totalTax), icon: "mdi:cash-check", color: "#ee4444", desc: "CGST + SGST + IGST" },
    ];

    const liabilityItems = [
        { label: "Integrated Tax (IGST)", value: fmt(totals.igst), color: "#f4a60a" },
        { label: "Central Tax (CGST)", value: fmt(totals.cgst), color: "#3dc4c4" },
        { label: "State / UT Tax (SGST)", value: fmt(totals.sgst), color: "#45c15d" },
        { label: "Net Tax Payable", value: fmt(totals.totalTax), color: "#487fff", highlight: true },
    ];

    return (
        <div className="card h-100">
            <div className="card-header no-print">
                <div className="row gy-3 align-items-end">
                    {/* Left — Filters */}
                    <div className="col-12 col-md-9 px-0">
                        <div className="d-flex flex-wrap align-items-end gap-3 px-0">
                            <div style={{ minWidth: "220px" }}>
                                <label className="form-label text-sm fw-semibold text-secondary-light mb-1">Business</label>
                                <BusinessDropdown
                                    businesses={sortedBusinesses}
                                    selectedCompanyId={selectedCompanyId}
                                    onBusinessChange={handleBusinessChange}
                                    onPricingNavigate={() => navigate("/pricing")}
                                    showAllOption={true}
                                    isAllowedCheck={(business) => {
                                        const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
                                        const allowedOwnIds = ownBusinesses
                                            .slice(-allowedBusinessCount)
                                            .map(b => b.companyId);

                                        return (
                                            business.isShared ||
                                            isAgencyPlan ||
                                            allowedOwnIds.includes(business.companyId)
                                        );
                                    }}
                                />
                            </div>
                            <div style={{ width: "160px" }}>
                                <CustomDropdown
                                    label="Month"
                                    value={selectedMonth}
                                    options={monthOptions}
                                    onSelect={setSelectedMonth}
                                />
                            </div>
                            <div style={{ width: "120px" }}>
                                <CustomDropdown
                                    label="Year"
                                    value={selectedYear}
                                    options={yearOptions}
                                    onSelect={setSelectedYear}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-3">
                        <div className="d-flex justify-content-md-end gap-2 flex-wrap">
                            <button
                                className="btn btn-sm btn-outline-primary-600 radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportPdf}
                                disabled={filteredInvoices.length === 0}
                            >
                                <Icon icon="mdi:file-pdf-outline" className="text-xl" />
                                Export PDF
                            </button>
                            <button
                                className="btn btn-sm btn-outline-success radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportGstr3BCASummary}
                                disabled={filteredInvoices.length === 0}
                            >
                                <Icon icon="mdi:file-export-outline" className="text-xl" />
                                Export for CA
                            </button>
                            <button
                                className="btn btn-sm btn-primary-600 radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportExcel}
                                disabled={filteredInvoices.length === 0}
                            >
                                <Icon icon="mdi:microsoft-excel" className="text-xl" />
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body">

                {/* Info Note for INR/GSTR-3B limitation */}
                <div className="alert alert-info d-flex align-items-center gap-2 mb-4 radius-8" role="alert" style={{ fontSize: "14px", border: "1px solid #487fff33", backgroundColor: "#487fff12", color: "#487fff" }}>
                    <Icon icon="lucide:info" className="fs-5" />
                    <div>
                        <strong>Note:</strong> GSTR-3B is an Indian tax report. Only invoices with INR currency are included in this report.
                    </div>
                </div>

                {/* ── Overview Cards ── */}
                <div className="row g-3 mb-4">
                    {overviewCards.map((card, i) => (
                        <div key={i} className="col-sm-6 col-md">
                            <div
                                className="radius-8 p-3 h-100 d-flex flex-column gap-2"
                                style={{
                                    border: `1px solid ${card.color}33`,
                                    background: `${card.color}12`,
                                }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="w-32-px h-32-px radius-6 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ background: `${card.color}22` }}
                                    >
                                        <Icon icon={card.icon} style={{ color: card.color, fontSize: 18 }} />
                                    </span>
                                    <span className="text-secondary-light" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                        {card.label}
                                    </span>
                                </div>
                                <p className="mb-0 fw-bold" style={{ color: card.color, fontSize: 15 }}>
                                    {sym} {card.value}
                                </p>
                                <p className="mb-0 text-secondary-light" style={{ fontSize: 11 }}>{card.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Section 3.1 Table ── */}
                <div className="mb-4">
                    <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                        <Icon icon="mdi:table" className="text-primary-600" />
                        Section 3.1 — Outward Supplies by Tax Rate
                    </h6>

                    {filteredInvoices.length === 0 ? (
                        <div className="text-center py-5">
                            <Icon icon="mdi:file-document-outline" className="text-4xl text-secondary-light opacity-50 mb-3" />
                            <p className="mb-0 text-secondary-light">No invoices found for the selected period.</p>
                        </div>
                    ) : (
                        <>
                            <style>{`
                            .hide-scrollbar-custom::-webkit-scrollbar { display: none; }
                            .hide-scrollbar-custom { -ms-overflow-style: none; scrollbar-width: none; }
                        `}</style>
                            <div className="table-responsive hide-scrollbar-custom">
                                <table className="table bordered-table text-sm">
                                    <thead>
                                        <tr>
                                            <th>Nature of Supply</th>
                                            <th>Invoices</th>
                                            <th>Taxable</th>
                                            <th>CGST</th>
                                            <th>SGST</th>
                                            <th>IGST</th>
                                            <th>Total Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {TAX_SLABS.map(slab => (
                                            <tr key={slab}>
                                                <td>
                                                    Outward Supplies{" "}
                                                    <span className="badge" style={{ background: "#487fff22", color: "#487fff", fontSize: 12 }}>
                                                        @ {slab}% GST
                                                    </span>
                                                </td>
                                                <td>{slabData[slab].count}</td>
                                                <td>{fmt(slabData[slab].taxableValue)}</td>
                                                <td>{fmt(slabData[slab].cgst)}</td>
                                                <td>{fmt(slabData[slab].sgst)}</td>
                                                <td>{fmt(slabData[slab].igst)}</td>
                                                <td className="fw-semibold">{fmt(slabData[slab].totalTax)}</td>
                                            </tr>
                                        ))}
                                        {slabData["other"].count > 0 && (
                                            <tr>
                                                <td>Other / Custom Rate</td>
                                                <td>{slabData["other"].count}</td>
                                                <td>{fmt(slabData["other"].taxableValue)}</td>
                                                <td>{fmt(slabData["other"].cgst)}</td>
                                                <td>{fmt(slabData["other"].sgst)}</td>
                                                <td>{fmt(slabData["other"].igst)}</td>
                                                <td className="fw-semibold">{fmt(slabData["other"].totalTax)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="fw-bold" style={{ borderTop: "2px solid var(--primary-200)" }}>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>Total</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{filteredInvoices.length}</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{fmt(totals.taxableValue)}</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{fmt(totals.cgst)}</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{fmt(totals.sgst)}</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{fmt(totals.igst)}</td>
                                            <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{fmt(totals.totalTax)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Tax Liability Summary ── */}
                <div className="radius-8 p-4 border">
                    <h6 className="fw-semibold mb-3">Tax Liability Summary (Section 6)</h6>
                    <div className="row g-3">
                        {liabilityItems.map((item, i) => (
                            <div key={i} className="col-sm-6 col-md-3">
                                <div
                                    className="p-3 radius-8"
                                    style={item.highlight
                                        ? { background: item.color, color: "#fff" }
                                        : { background: `${item.color}12`, border: `1px solid ${item.color}33` }
                                    }
                                >
                                    <p className="text-xs mb-1" style={{ color: item.highlight ? "rgba(255,255,255,0.75)" : "var(--secondary-light)" }}>
                                        {item.label}
                                    </p>
                                    <p className="mb-0 fw-bold" style={{ fontSize: 15, color: item.highlight ? "#fff" : item.color }}>
                                        {sym} {item.value}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Disclaimer ── */}
                <p className="text-xs text-secondary-light mt-3 mb-0 d-flex align-items-center gap-1">
                    <Icon icon="mdi:information-outline" />
                    This is a summary report generated from your invoice data. Please verify with your CA before filing.
                </p>
            </div>
        </div>
    );
};

export default Gstr3BLayer;
